import { useState, useCallback, useEffect } from 'react';
import {
    Scheme,
    ClientCategory,
    ClientGroup,
    Distributor,
    ClientClass,
} from '../../../types/client';
import {
    supportingDataApi,
    companiesApi,
    fieldConfigApi
} from '../../../services/api';
import { toast } from 'react-toastify';
import type { FilterOption } from '@/components/ui';

interface CompanyOption {
    id: number;
    label: string;
    name?: string;
    symbol?: string;
}

export const useClientSupportingData = (selectedClientType: 'PMS' | 'AIF') => {
    const [schemes, setSchemes] = useState<Scheme[]>([]);
    const [categories, setCategories] = useState<ClientCategory[]>([]);
    const [groups, setGroups] = useState<ClientGroup[]>([]);
    const [clientClasses, setClientClasses] = useState<ClientClass[]>([]);
    const [distributors, setDistributors] = useState<Distributor[]>([]);
    const [countries, setCountries] = useState<any[]>([]);
    const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
    const [requiredFields, setRequiredFields] = useState<string[]>([]);
    const [fieldConfigs, setFieldConfigs] = useState<any[]>([]);
    const [dataLoading, setDataLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [commonDataLoaded, setCommonDataLoaded] = useState(false);

    const loadCommonData = useCallback(async (signal?: AbortSignal) => {
        if (commonDataLoaded) return;
        try {
            setDataLoading(true);
            const [
                schemesData,
                categoriesData,
                groupsData,
                clientClassesData,
                distributorsData,
                countriesData,
            ] = await Promise.all([
                supportingDataApi.getSchemes({ signal }),
                supportingDataApi.getClientCategories({ signal }),
                supportingDataApi.getClientGroups({ signal }),
                supportingDataApi.getClientClasses({ signal }),
                supportingDataApi.getDistributors({ signal }),
                supportingDataApi.getCountries({ signal }),
            ]);

            if (signal?.aborted) return;

            setSchemes(schemesData.filter((s: any) => s.is_active));
            setCategories(categoriesData.filter((c: any) => c.is_active));
            setGroups(groupsData.filter((g: any) => g.is_active));
            setClientClasses(clientClassesData.filter((c: any) => c.is_active));
            setDistributors(distributorsData.filter((d: any) => d.is_active));
            setCountries(countriesData);
            setCommonDataLoaded(true);

        } catch (err: any) {
            if (err && err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
                setError('Failed to load supporting data');
                toast.error('Failed to load supporting data');
            }
        } finally {
            if (!signal?.aborted) {
                // Keep loading true until config is also loaded
            }
        }
    }, [commonDataLoaded]);

    const loadFieldConfig = useCallback(async (type: 'PMS' | 'AIF', signal?: AbortSignal) => {
        try {
            setDataLoading(true);
            const configsData = await fieldConfigApi.getByClientType(type, { signal }).catch(() => []);

            if (signal?.aborted) return;

            setFieldConfigs(configsData);

            // Extract required fields solely from configs
            const configRequired = configsData.filter((c: any) => c.is_required).map((c: any) => c.field_name);

            if (configRequired.length > 0) {
                setRequiredFields(configRequired);
            } else {
                // Fallback only if no configurations are returned at all
                setRequiredFields([
                    'client_code',
                    'first_name',
                    'last_name',
                    'contact_no',
                    'fa_account_no',
                    'scheme',
                    'category',
                    // Client Class is required but AIF-only; Group is optional for both.
                    ...(type === 'AIF' ? ['client_class'] : []),
                ]);
            }
        } catch (err: any) {
            if (err && err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
                console.error('Failed to load field configs');
            }
        } finally {
            if (!signal?.aborted) {
                setDataLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        loadCommonData(controller.signal);
        return () => controller.abort();
    }, [loadCommonData]);

    useEffect(() => {
        const controller = new AbortController();
        loadFieldConfig(selectedClientType, controller.signal);
        return () => controller.abort();
    }, [selectedClientType, loadFieldConfig]);

    // loadOptions for the restricted-companies picker. It must not touch form state:
    // re-rendering the picker's value mid-search makes MUI clear the typed text.
    const fetchCompanies = useCallback(async (query: string, signal?: AbortSignal): Promise<FilterOption[]> => {
        try {
            const response = await companiesApi.getCompanies({ search: query, page_size: 100, is_active: true }, { signal });
            if (signal?.aborted) return [];
            return (response.results || []).map((c: any) => ({ value: c.id.toString(), label: `${c.name} (${c.symbol})` }));
        } catch (err: any) {
            if (err && err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
                toast.error('Failed to fetch companies');
            }
            return [];
        }
    }, []);

    return {
        schemes,
        categories,
        groups,
        clientClasses,
        distributors,
        countries,
        companyOptions,
        setCompanyOptions,
        fetchCompanies,
        requiredFields,
        fieldConfigs,
        dataLoading,
        error,
        setDataLoading
    };
};
